import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/services/api';

interface PhotoUploadDialogProps {
  prisonerId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function PhotoUploadDialog({ prisonerId, isOpen, onClose }: PhotoUploadDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isPrimary, setIsPrimary] = useState(true);
  const [description, setDescription] = useState('');
  const [takenAt, setTakenAt] = useState(new Date().toISOString().split('T')[0]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Keine Datei ausgewählt');
      return api.uploadPhoto(prisonerId, file, {
        isPrimary,
        takenAt,
        description: description || undefined,
      });
    },
    onSuccess: () => {
      toast({
        title: 'Foto hochgeladen',
        description: 'Das Foto wurde erfolgreich gespeichert.',
      });
      // Invalidate all relevant queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['prisoner-profile', prisonerId] });
      queryClient.invalidateQueries({ queryKey: ['prisoner', prisonerId] });
      queryClient.invalidateQueries({ queryKey: ['prisoners'] });
      handleClose();
    },
    onError: (error: Error) => {
      toast({
        title: 'Fehler beim Hochladen',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setIsPrimary(true);
    setDescription('');
    setTakenAt(new Date().toISOString().split('T')[0]);
    onClose();
  };

  const removeFile = () => {
    setFile(null);
    setPreview(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Foto hochladen</DialogTitle>
          <DialogDescription>
            Laden Sie ein erkennungsdienstliches Lichtbild hoch. Unterstützte Formate: JPEG, PNG, GIF, WebP (max. 10MB)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Dropzone */}
          {!preview ? (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}`}
            >
              <input {...getInputProps()} />
              <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              {isDragActive ? (
                <p className="text-primary">Datei hier ablegen...</p>
              ) : (
                <div>
                  <p className="text-muted-foreground">
                    Datei hierher ziehen oder klicken zum Auswählen
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    JPEG, PNG, GIF, WebP (max. 10MB)
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="relative">
              <div className="flex justify-center">
                <div className="relative w-48 h-64 rounded-lg overflow-hidden border">
                  <img
                    src={preview}
                    alt="Vorschau"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2"
                onClick={removeFile}
              >
                <X className="h-4 w-4" />
              </Button>
              <p className="text-center text-sm text-muted-foreground mt-2">
                {file?.name} ({(file?.size || 0 / 1024).toFixed(1)} KB)
              </p>
            </div>
          )}

          {/* Metadata */}
          <div className="grid gap-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isPrimary"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="isPrimary">Als Hauptbild festlegen</Label>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="takenAt">Aufnahmedatum</Label>
              <Input
                id="takenAt"
                type="date"
                value={takenAt}
                onChange={(e) => setTakenAt(e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Beschreibung (optional)</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="z.B. Erkennungsdienstliches Lichtbild"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Abbrechen
          </Button>
          <Button 
            onClick={() => uploadMutation.mutate()}
            disabled={!file || uploadMutation.isPending}
          >
            {uploadMutation.isPending ? 'Wird hochgeladen...' : 'Hochladen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
